/**
 * Mantine `<Select>` wrapper that loads its options from `/v1/sites`.
 *
 * Used wherever an operator has to pick a site (admin filters and the
 * create-mode `site_slug` field on the meter / device form modals).
 * Replacing the bare `<TextInput>` with this:
 *   - removes a class of typo-driven 404s ("ess-hz-nl" vs. "ess-nl-hz"),
 *   - lets the operator scan a familiar display name instead of guessing
 *     a slug they may not have memorised,
 *   - keeps the underlying value still a slug, so the upstream filter /
 *     create-body shape doesn't change.
 *
 * The list is fetched with `limit: 200` — that's the API's hard cap and
 * comfortably covers any realistic tenant. If we ever need more we'll
 * graduate this to an async-search combobox; until then a single page
 * load is the cheapest UX.
 */
import { Select, type SelectProps } from "@mantine/core";
import { useMemo } from "react";

import { useSites } from "@/api/queries";

export type SiteSelectProps = Omit<SelectProps, "data">;

export function SiteSelect(props: SiteSelectProps) {
  const sitesQuery = useSites({ limit: 200 });

  const data = useMemo(() => {
    const items = sitesQuery.data?.items ?? [];
    return items.map((site) => ({
      value: site.slug,
      // Operators recognise sites by display name, not slug. Show both
      // so a human can scan and the slug still appears verbatim for
      // anyone who's mid-keyboard.
      label:
        site.display_name && site.display_name !== site.slug
          ? `${site.display_name} (${site.slug})`
          : site.slug,
    }));
  }, [sitesQuery.data]);

  // Surface a load error in the description so the operator knows why
  // the dropdown is empty rather than silently showing "No sites match".
  const errorDescription =
    sitesQuery.error instanceof Error
      ? `Failed to load sites: ${sitesQuery.error.message}`
      : undefined;

  return (
    <Select
      searchable
      placeholder={sitesQuery.isLoading ? "Loading sites…" : "Pick a site"}
      nothingFoundMessage="No sites match"
      {...props}
      data={data}
      disabled={sitesQuery.isLoading || props.disabled}
      description={errorDescription ?? props.description}
    />
  );
}
